import React, { useEffect, useRef, useState } from 'react';
import { CaretDownIcon } from '@buzzfeed/react-components';
import NewsletterForm from '../../components/NewsletterForm';
import Footer from '../../components/Footer';
import Explore from '../../components/Explore';
import Button from '../../components/Button';
import { useTrackingContext } from '../../hooks';
import useGifReplay from '../../hooks/useGifReplay';
import styles from './index.module.scss';
import rainbowSeagull from '../../public/static/images/rainbowSeagull.gif';

export default function SignupPage() {
  const detailsRef = useRef(null);
  const imageRef = useRef(null);
  const { trackPageview, trackContentAction } = useTrackingContext();
  const [windowHeight, setWindowHeight] = useState(0);

  // Custom hook to replay rainbow seagull GIF animation when it re-enters viewport
  const gifKey = useGifReplay(imageRef);

  useEffect(() => {
    trackPageview();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    // Set initial height
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onSubmitSuccess = () => {
    trackContentAction({
      action_type: 'signup',
      action_value: 'buzzfeed_email_island',
      item_name: 'submission',
      item_type: 'button',
      position_in_unit: 0,
      subunit_name: '',
      subunit_type: '',
      unit_name: 'main',
      unit_type: 'feed',
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.overlay} />
        <div className={styles.signUpWrapper}>
          <a href="/" className={styles.homeLink}>
            <h1 className={styles.visuallyHidden}>BuzzFeed Island - Join Us</h1>
          </a>
          <div className={styles.heroSection} id="hero-section">
            <div className={styles.textWrapper}>
              <div className={styles.text}>
                <span className={styles.section1}>Sign up for</span>
                <span className={styles.section2}>BF Island.</span>
                <span className={styles.section3}>
                  Join the waitlist for exclusive access to the app that helps
                  you make wild and hilarious creations to light up your group
                  chat. Sign up for an invite code below.
                </span>
                <div className={styles.signupPanel} id="signup">
                  <NewsletterForm onSubmitSuccess={onSubmitSuccess} />
                </div>
              </div>
            </div>
            {/* Rainbow seagull GIF - key prop forces re-render to replay animation */}
            <div className={styles.imgWrapper}>
              <img
                ref={imageRef}
                key={gifKey}
                src={`${rainbowSeagull}?t=${gifKey}`}
                alt=""
                className={styles.heroImage}
              />
            </div>
          </div>

          <div
            className={`${styles.learnMoreLink} ${windowHeight >= 980 ? styles.hidden : styles.visible}`}
          >
            <button
              type="button"
              className={styles.text}
              onClick={() => {
                if (detailsRef.current) {
                  const el = detailsRef.current;
                  const y =
                    el.getBoundingClientRect().top + window.pageYOffset - 100;
                  window.scrollTo({ top: y, behavior: 'smooth' });
                }
              }}
              aria-controls="learn-more-details"
            >
              <span>Learn more</span>
              <CaretDownIcon className={styles.arrow} />
            </button>
          </div>
        </div>
        <Explore detailsRef={detailsRef} />
        <div className={styles.bottomSection}>
          <div>Spots are limited.</div>
          <div>Be among the first to set foot on the Island.</div>

          <Button
            className={styles.bottomButton}
            onClick={(e) => {
              e.preventDefault();
              const signupPanel = document.getElementById('hero-section');
              if (signupPanel) {
                const y =
                  signupPanel.getBoundingClientRect().top +
                  window.pageYOffset -
                  100;
                window.scrollTo({ top: y, behavior: 'smooth' });
              }
            }}
            text="Claim your place"
            height={38}
            backgroundColor="#0F0"
            shadowColor="#00D900"
            color="black"
          />
        </div>
        <Footer />
      </div>
    </div>
  );
}

export async function getServerSideProps() {
  return {
    props: {
      tracking: {
        context_page_id: 'signup',
        context_page_type: 'utility',
      },
    },
  };
}
